import { useMemo } from 'react';

import { Button, LoadingBubbles, Typography, useSnackbar } from '@scaleaq/scaleui';
import { SiteControlEvent } from '@scaleaq/sitecontrol-lib';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {
  ColumnType,
  resolveColumnAccessorFn,
  siteControlTableFeatures,
  SiteControlTableFeatures,
} from '@/components/table/';

import { getColumns } from './eventWidget/getColumns.tsx';
import { HistoricalEvents } from './eventWidget/HistoricalEvents.tsx';
import { LiveEvents } from './eventWidget/LiveEvents.tsx';
import { useInventoryContext } from './InventoryProvider/InventoryContext';
import { EventTableConfig, EventTableType } from './types.ts';

type EventTableProps = {
  id: string;
  config: EventTableConfig;
  defaultTableConfig: EventTableConfig;
};

export const EventTable = ({ id, config, defaultTableConfig }: EventTableProps) => {
  const { t } = useTranslation(['common', 'eventStream']);
  const { enqueueSnackbar } = useSnackbar();

  const isHistorical = useMemo(() => config.type === EventTableType.HISTORICAL, [config.type]);
  const { setEventId, inventory, isFetching } = useInventoryContext();

  const columns: ColumnType<SiteControlEvent>[] = useMemo(() => {
    const columns = getColumns({
      inventory,
      inventoryLoading: isFetching,
      t,
      clipboardCallback: (value: string) => {
        enqueueSnackbar(t('eventStream:copiedToClipboard', { value }), { variant: 'success' });
      },
    });
    return [
      ...columns,
      {
        header: '',
        type: 'display',
        accessorKey: 'view',
        cell: (cell: CellContext<SiteControlTableFeatures, SiteControlEvent, unknown>) => (
          <Button
            style={{ width: 'max-content' }}
            onClick={() => {
              setEventId(cell.row.original.id);
            }}
          >
            {t('common:view')}
          </Button>
        ),
      },
    ];
  }, [enqueueSnackbar, inventory, isFetching, setEventId, t]);

  // Which columns can be sorted in historical mode
  const sortColumns = useMemo(() => {
    if (isHistorical) {
      return ['timestamp'];
    }
    return ['timestamp', 'type', 'sender', 'sourcePath'];
  }, [isHistorical]);

  // Which columns can be filtered in historical mode
  const filterColumns = useMemo(() => {
    if (isHistorical) {
      return ['type', 'sourcePath'];
    }
    return ['timestamp', 'type', 'sender', 'sourcePath'];
  }, [isHistorical]);
  const columnHelper = createColumnHelper<typeof siteControlTableFeatures, SiteControlEvent>();
  const tableColumns = useMemo(
    () =>
      columns.map(c => {
        if (c.type === 'accessor') {
          return columnHelper.accessor(resolveColumnAccessorFn(c), {
            id: c.accessorKey,
            header: () => <Typography>{c.header}</Typography>,
            ...(c.cell && { cell: c.cell }),
            meta: c.meta,
            enableSorting: sortColumns.includes(c.accessorKey),
            enableColumnFilter: filterColumns.includes(c.accessorKey),
          });
        }
        return columnHelper.display({
          id: c.accessorKey,
          header: () => <Typography>{c.header}</Typography>,
          cell: c?.cell,
        });
      }),
    [columnHelper, columns, filterColumns, sortColumns]
  );

  if (isFetching) {
    return <LoadingBubbles />;
  }

  return (
    <>
      {config.type === EventTableType.LIVE && (
        <LiveEvents
          configInput={config}
          columns={columns}
          defaultTableConfig={defaultTableConfig}
          id={id}
          inventory={inventory}
        />
      )}
      {config.type === EventTableType.HISTORICAL && (
        <HistoricalEvents
          configInput={config}
          columns={columns}
          defaultTableConfig={defaultTableConfig}
          id={id}
          tableColumns={tableColumns}
        />
      )}
    </>
  );
};
